import React, { useState } from 'react';
import { motion } from 'framer-motion';
import API_BASE_URL from '../services/config';

const Feedback = () => {
  const [feedback, setFeedback] = useState({
    name: '',
    email: '',
    type: 'suggestion',
    message: '',
    rating: 5,
    allowContact: false
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const feedbackTypes = [
    { value: 'suggestion', label: '💡 Suggestion', description: 'Ideas to improve the platform' },
    { value: 'bug', label: '🐛 Bug Report', description: 'Something is not working properly' },
    { value: 'content', label: '📚 Content Feedback', description: 'About scriptures or answers' },
    { value: 'feature', label: '🚀 Feature Request', description: 'New functionality ideas' },
    { value: 'general', label: '💬 General Feedback', description: 'Other thoughts and comments' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: feedback.name.trim(),
          email: feedback.email.trim(),
          type: feedback.type,
          message: feedback.message.trim(),
          rating: feedback.rating,
          allowContact: feedback.allowContact
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.message || `Failed to submit feedback (${response.status})`
        );
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Feedback submit error:', error);
      setSubmitError(
        error.message || 'We could not submit your feedback right now. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFeedback({
      name: '',
      email: '',
      type: 'suggestion',
      message: '',
      rating: 5,
      allowContact: false
    });
    setSubmitted(false);
    setSubmitError('');
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-green-500 to-teal-600 rounded-full mb-8 shadow-2xl">
              <span className="text-4xl">🎉</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-400 to-teal-400 bg-clip-text text-transparent mb-6">
              Thank You!
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Your feedback has been received. We truly appreciate you taking the time to help us improve Dharma Guide.
            </p>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 max-w-md mx-auto">
              <div className="text-6xl mb-4">⭐</div>
              <p className="text-gray-300 mb-6">
                Your input helps us serve the community better and make spiritual wisdom more accessible.
              </p>
              <button
                onClick={handleReset}
                className="bg-gradient-to-r from-green-500 to-teal-500 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
              >
                Submit More Feedback
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-2xl mb-6 shadow-2xl">
            <span className="text-2xl">📝</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-yellow-400 to-amber-400 bg-clip-text text-transparent mb-4">
            Share Feedback
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Help us improve Dharma Guide. Your insights make spiritual wisdom more accessible to everyone.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700"
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                {submitError && (
                  <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                    {submitError}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Your Name
                    </label>
                    <input
                      type="text"
                      value={feedback.name}
                      onChange={(e) => setFeedback({...feedback, name: e.target.value})}
                      placeholder="Optional"
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={feedback.email}
                      onChange={(e) => setFeedback({...feedback, email: e.target.value})}
                      placeholder="Optional - for follow up"
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all duration-300"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-4">
                    Feedback Type
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {feedbackTypes.map((type) => (
                      <label
                        key={type.value}
                        className={`relative flex items-start p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                          feedback.type === type.value
                            ? 'border-yellow-500 bg-yellow-500/10'
                            : 'border-gray-600 bg-gray-700/30 hover:border-gray-500'
                        }`}
                      >
                        <input
                          type="radio"
                          name="feedbackType"
                          value={type.value}
                          checked={feedback.type === type.value}
                          onChange={(e) => setFeedback({...feedback, type: e.target.value})}
                          className="sr-only"
                        />
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            feedback.type === type.value
                              ? 'border-yellow-500 bg-yellow-500'
                              : 'border-gray-400'
                          }`}>
                            {feedback.type === type.value && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                          <div>
                            <div className="font-semibold text-white">{type.label}</div>
                            <div className="text-sm text-gray-400">{type.description}</div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Overall Rating
                  </label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFeedback({...feedback, rating: star})}
                        className="text-2xl transition-transform duration-200 hover:scale-110"
                      >
                        {star <= feedback.rating ? '⭐' : '☆'}
                      </button>
                    ))}
                    <span className="ml-2 text-gray-300 font-semibold">
                      {feedback.rating}/5
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Your Feedback *
                  </label>
                  <textarea
                    required
                    rows={6}
                    value={feedback.message}
                    onChange={(e) => setFeedback({...feedback, message: e.target.value})}
                    placeholder="Please share your thoughts, suggestions, or describe any issues you've encountered..."
                    className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all duration-300 resize-vertical"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="allowContact"
                    checked={feedback.allowContact}
                    onChange={(e) => setFeedback({...feedback, allowContact: e.target.checked})}
                    className="w-4 h-4 text-yellow-500 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500 focus:ring-2"
                  />
                  <label htmlFor="allowContact" className="text-sm text-gray-300">
                    I'm open to being contacted for follow-up questions about my feedback
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 text-gray-900 py-4 rounded-xl font-semibold text-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? 'Submitting Feedback...' : 'Submit Feedback'}
                </button>
              </form>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Impact Stats */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700"
            >
              <h3 className="text-lg font-bold text-white mb-4">Your Impact</h3>
              <div className="space-y-4">
                {[
                  { icon: '👥', text: '1,200+ feedback items implemented' },
                  { icon: '🚀', text: '45+ features inspired by user suggestions' },
                  { icon: '📚', text: 'Improved scripture accuracy and relevance' },
                  { icon: '💫', text: 'Better experience for all spiritual seekers' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="text-2xl">{item.icon}</div>
                    <div className="text-sm text-gray-300">{item.text}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* What Happens Next */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-2xl p-6 border border-blue-500/20"
            >
              <h3 className="text-lg font-bold text-white mb-3">What Happens Next?</h3>
              <div className="space-y-3 text-sm text-gray-300">
                <div className="flex items-start gap-2">
                  <span className="text-blue-400 font-bold">1.</span>
                  <span>We review all feedback within 48 hours</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-400 font-bold">2.</span>
                  <span>Priority given to impactful suggestions</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-400 font-bold">3.</span>
                  <span>Implementation planning with development team</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-400 font-bold">4.</span>
                  <span>Updates shared in platform announcements</span>
                </div>
              </div>
            </motion.div>

            {/* Quick Feedback Options */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="bg-gradient-to-br from-green-500/10 to-teal-500/10 rounded-2xl p-6 border border-green-500/20"
            >
              <h3 className="text-lg font-bold text-white mb-3">Quick Feedback</h3>
              <p className="text-sm text-gray-300 mb-4">
                Have a quick suggestion? Choose from common categories:
              </p>
              <div className="space-y-2">
                {[
                  'More scripture translations',
                  'Mobile app request',
                  'New topic categories',
                  'Video explanations',
                  'Community features'
                ].map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => setFeedback({
                      ...feedback,
                      type: 'suggestion',
                      message: `I'd like to see: ${suggestion}`
                    })}
                    className="w-full text-left p-3 rounded-lg bg-gray-700/30 hover:bg-gray-600/30 text-sm text-gray-300 transition-all duration-200"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Feedback;
